import React, { useState } from "react";
import { motion } from "framer-motion";
import { sendContactEmail } from "../utils/emailConfig";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const result = await sendContactEmail(formData);
      if (result.success) {
        setSubmitStatus({
          type: "success",
          message: "Thank you for your message! We will get back to you soon.",
        });
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      } else {
        setSubmitStatus({
          type: "error",
          message: "Failed to send message. Please try again later.",
        });
      }
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message: "An error occurred. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      title: "Address",
      content: "3609-C Chain Bridge Road, Fairfax, VA 22031",
      accentColor: "#C72631",
    },
    {
      title: "Phone",
      content: "833-383-3783",
      accentColor: "#C72631",
    },
    {
      title: "Email",
      content: "info@3etainment.com",
      accentColor: "#C72631",
    },
  ];

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="hero-overlay" />
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            Contact Us
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            Whether you are planning a partnership, booking an artist, exploring
            sponsorship, or reaching out for media inquiries, we would love to
            hear from you.
          </motion.p>
        </motion.div>
      </section>

      {/* Contact Info Cards */}
      <section className="contact-info-section">
        <div className="info-cards-container">
          {contactInfo.map((info, index) => (
            <motion.div
              key={info.title}
              className="info-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
            >
              <div
                className="info-accent"
                style={{ background: info.accentColor }}
              />
              <h3>{info.title}</h3>
              <p>{info.content}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="main-contact-section">
        <div className="contact-container">
          {/* Contact Form */}
          <motion.div
            className="form-wrapper"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="form-header">
              <h2>We'd Love to Hear From You</h2>
              <p>
                Fill out the form below and we'll get back to you as soon as
                possible.
              </p>
            </div>

            {/* Always mounted so screen readers announce the result when it arrives. */}
            <div role="status" aria-live="polite" aria-atomic="true">
              {submitStatus && (
                <motion.div
                  className={`status-message ${submitStatus.type}`}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {submitStatus.message}
                </motion.div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="contact-form" aria-busy={isSubmitting}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="contact-name">Full Name *</label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    autoComplete="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="contact-email">Email Address *</label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="contact-phone">Phone Number</label>
                  <input
                    type="tel"
                    id="contact-phone"
                    name="phone"
                    autoComplete="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(123) 456-7890"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="contact-subject">Subject *</label>
                  <input
                    type="text"
                    id="contact-subject"
                    name="subject"
                    autoComplete="off"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="How can we help?"
                  />
                </div>
              </div>

              <div className="form-group full-width">
                <label htmlFor="contact-message">Message *</label>
                <textarea
                  id="contact-message"
                  name="message"
                  autoComplete="off"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  placeholder="Tell us more about your inquiry..."
                />
              </div>

              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner" /> Sending...
                  </>
                ) : (
                  <>
                    Send Message <span className="btn-arrow">→</span>
                  </>
                )}
              </button>
            </form>
          </motion.div>

          {/* Contact Side Info */}
          <motion.div
            className="side-info"
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="community-card">
              <h3>Join Our Community</h3>
              <p>
                3E Entertainment is committed to building experiences that
                connect culture, community, and purpose. Whether you are
                partnering with us, booking an artist, or simply staying
                connected, we would love to hear from you.
              </p>
              <div className="social-links">
                <a href="#" className="social-link" aria-label="Facebook">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
                <a href="#" className="social-link" aria-label="Instagram">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>
                <a href="#" className="social-link" aria-label="YouTube">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </a>
              </div>
            </div>

            <div className="office-hours">
              <h4>Office Hours</h4>
              <ul>
                <li>
                  <span>Monday - Friday</span>
                  <span>9:00 AM - 6:00 PM</span>
                </li>
                <li>
                  <span>Saturday</span>
                  <span>10:00 AM - 4:00 PM</span>
                </li>
                <li>
                  <span>Sunday</span>
                  <span>Closed</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <motion.div
          className="map-container"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3105.150650490597!2d-77.3072!3d38.8512!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89b64e6a1c3b3b3b%3A0x1b1b1b1b1b1b1b1b!2s3609%20C%20Chainbridge%20Rd%2C%20Fairfax%2C%20VA%2022030!5e0!3m2!1sen!2sus!4v1620000000000!5m2!1sen!2sus"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="3E Entertainment Location"
          />
          <div className="map-info-card">
            <h4>3E Entertainment</h4>
            <p>
              3609-C Chain Bridge Road
              <br />
              Fairfax, VA 22031
            </p>
          </div>
        </motion.div>
      </section>

      <style>{`
        .contact-page {
          overflow-x: hidden;
        }

        /* Hero Section */
        .contact-hero {
          position: relative;
          min-height: 60vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: url("https://images.unsplash.com/photo-1544735716-392fe2489ffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80") center/cover no-repeat;
        }

        .hero-overlay {
          position: absolute;
          inset: 0;
          background: rgba(10, 10, 10, 0.85);
        }

        .hero-content {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 800px;
          padding: 0 2rem;
        }


        .hero-content h1 {
          font-size: clamp(2.5rem, 6vw, 4rem);
          font-weight: 900;
          color: white;
          margin: 0 0 1rem;
          line-height: 1.1;
        }

        .hero-content p {
          font-size: 1.2rem;
          color: rgba(255, 255, 255, 0.9);
          line-height: 1.7;
        }

        /* Contact Info Cards */
        .contact-info-section {
          background: #fff;
          padding: 0;
          margin-top: -50px;
          position: relative;
          z-index: 10;
        }

        .info-cards-container {
          max-width: 1000px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
          padding: 0 2rem;
        }

        .info-card {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          text-align: center;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.08);
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          position: relative;
          overflow: hidden;
          border: 1px solid #f0f0f0;
        }

        .info-card:hover {
          border-color: transparent;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
        }

        .info-accent {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
        }

        .info-card h3 {
          font-size: 0.85rem;
          font-weight: 700;
          color: #0a0a0a;
          margin: 0 0 0.5rem;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .info-card p {
          font-size: 1rem;
          color: #666;
          margin: 0;
          line-height: 1.5;
        }

        /* Main Contact Section */
        .main-contact-section {
          padding: 6rem 2rem;
          background: #fff;
        }

        .contact-container {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1.5fr 1fr;
          gap: 4rem;
          align-items: start;
        }

        .form-wrapper {
          background: white;
          border-radius: 20px;
          padding: 3rem;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.08);
          border: 1px solid #f0f0f0;
        }

        .form-header {
          margin-bottom: 2rem;
        }


        .form-header h2 {
          font-size: 1.8rem;
          font-weight: 900;
          color: #0a0a0a;
          margin: 0 0 0.5rem;
        }

        .form-header p {
          color: #666;
          font-size: 1rem;
          margin: 0;
        }

        .status-message {
          padding: 1rem 1.5rem;
          border-radius: 10px;
          margin-bottom: 1.5rem;
          font-weight: 600;
          font-size: 0.95rem;
        }

        .status-message.success {
          background: #e8f5e9;
          color: #2e7d32;
          border: 1px solid #c8e6c9;
        }

        .status-message.error {
          background: #ffebee;
          color: #c62828;
          border: 1px solid #ffcdd2;
        }

        .contact-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-group.full-width {
          grid-column: 1 / -1;
        }

        .form-group label {
          font-size: 0.9rem;
          font-weight: 600;
          color: #0a0a0a;
        }

        .form-group input,
        .form-group textarea {
          padding: 1rem 1.25rem;
          border: 2px solid #e9ecef;
          border-radius: 10px;
          font-size: 1rem;
          color: #0a0a0a;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          background: #fafafa;
        }

        .form-group input:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #C72631;
          background: white;
          box-shadow: 0 0 0 4px rgba(199, 38, 49, 0.08);
        }

        .form-group input::placeholder,
        .form-group textarea::placeholder {
          color: #adb5bd;
        }

        .form-group textarea {
          resize: vertical;
          min-height: 120px;
        }

        .submit-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 1.25rem 2.5rem;
          background: #C72631;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-3px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .btn-arrow {
          transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .submit-btn:hover .btn-arrow {
          transform: translateX(5px);
        }

        .spinner {
          width: 18px;
          height: 18px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Side Info */
        .side-info {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .community-card {
          background: white;
          border: 2px solid #C72631;
          border-radius: 16px;
          padding: 2rem;
        }

        .community-card h3 {
          font-size: 1.4rem;
          font-weight: 900;
          color: #C72631;
          margin: 0 0 1rem;
        }

        .community-card p {
          color: #666;
          line-height: 1.7;
          margin: 0 0 1.5rem;
          font-size: 0.95rem;
        }

        .social-links {
          display: flex;
          gap: 0.75rem;
        }

        .social-link {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: #C72631;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .social-link:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
        }

        .office-hours {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.06);
          border: 1px solid #f0f0f0;
        }

        .office-hours h4 {
          font-size: 1.1rem;
          font-weight: 900;
          color: #0a0a0a;
          margin: 0 0 1.5rem;
        }

        .office-hours ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .office-hours li {
          display: flex;
          justify-content: space-between;
          padding: 0.75rem 0;
          border-bottom: 1px solid #f0f0f0;
          font-size: 0.9rem;
        }

        .office-hours li:last-child {
          border-bottom: none;
        }

        .office-hours li span:first-child {
          color: #666;
        }

        .office-hours li span:last-child {
          color: #0a0a0a;
          font-weight: 600;
        }

        /* Map Section */
        .map-section {
          padding: 0;
        }

        .map-container {
          position: relative;
          width: 100%;
          height: 400px;
          overflow: hidden;
        }

        .map-container iframe {
          filter: grayscale(0.2) contrast(1.1);
        }

        .map-info-card {
          position: absolute;
          top: 20px;
          left: 20px;
          background: white;
          padding: 1.5rem;
          border-radius: 12px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
          z-index: 10;
        }

        .map-info-card h4 {
          font-size: 1rem;
          font-weight: 700;
          color: #C72631;
          margin: 0 0 0.5rem;
        }

        .map-info-card p {
          color: #666;
          font-size: 0.9rem;
          line-height: 1.5;
          margin: 0;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .contact-container {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .info-cards-container {
            grid-template-columns: 1fr;
            max-width: 400px;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .form-wrapper {
            padding: 2rem;
          }

          .main-contact-section {
            padding: 4rem 1.5rem;
          }
        }

        @media (max-width: 480px) {
          .contact-hero {
            min-height: 50vh;
          }

          .map-container {
            height: 300px;
          }

          .map-info-card {
            left: 10px;
            right: 10px;
            top: auto;
            bottom: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default Contact;
